import React, { useRef } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Scrollbar } from 'swiper';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  brandColours,
  fontWeights,
  fluidFontSize,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Heading, Slider, PrevButton, NextButton } from './ui';

const StyledDisplayStands = styled.section`
  ${sectionMargins()};
  overflow: hidden;
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 30px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 60px;
  `}
`;

const StyledOuter = styled.div`
  position: relative;
`;

const StyledContainer = styled(Container)`
  max-width: 840px;
`;

const Styledtems = styled(Slider)`
  overflow: visible;

  .swiper-slide {
    transition: ${standardTransition('transform')};

    &:not(.swiper-slide-active) {
      transform: scale(0.7);
    }
  }

  .swiper-scrollbar {
    position: static;
    margin: 30px auto 0;
    height: 5px;
    max-width: 312px;

    ${minBreakpointQuery.small`
      margin-top: 40px;
    `}

    ${minBreakpointQuery.medium`
      margin-top: 50px;
    `}

    ${minBreakpointQuery.large`
      margin-top: 60px;
    `}

    ${minBreakpointQuery.xxlarge`
      margin-top: 70px;
    `}

    ${minBreakpointQuery.xxxxlarge`
      margin-top: 80px;
    `}

    .swiper-scrollbar-drag {
      background-color: ${brandColours.primary};
    }
  }
`;

const StyledItem = styled.div``;

const StyledText = styled.p`
  margin-top: 20px;
  color: ${brandColours.primary};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '25px',
    '35px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge
  )};
  text-align: center;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const commonButtonStyles = () => {
  return css`
    ${maxBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}
  `;
};

const StyledPrevButton = styled(PrevButton)`
  ${commonButtonStyles()};
`;

const StyledNextButton = styled(NextButton)`
  ${commonButtonStyles()};
`;

const DisplayStands = ({ heading, items }) => {
  const sliderRef = useRef();

  const sliderOptions = {
    loop: true,
    loopedSlides: 2,
    modules: [Scrollbar],
    scrollbar: { draggable: true },
    onBeforeInit: swiper => {
      sliderRef.current = swiper;
    },
  };

  return (
    <StyledDisplayStands>
      <Container wide={true}>
        <StyledHeading alt={true}>{heading}</StyledHeading>
        <StyledOuter>
          <StyledContainer>
            <Styledtems {...sliderOptions}>
              {items.map(({ image, text }, id) => (
                <StyledItem key={id}>
                  <GatsbyImage image={image.gatsbyImageData} alt={image.alt} />
                  <StyledText>{text}</StyledText>
                </StyledItem>
              ))}
            </Styledtems>
          </StyledContainer>
          <StyledPrevButton onClick={() => sliderRef.current?.slidePrev()} />
          <StyledNextButton onClick={() => sliderRef.current?.slideNext()} />
        </StyledOuter>
      </Container>
    </StyledDisplayStands>
  );
};

export default DisplayStands;
